import i18n from "i18n";

/** Mirrors backend ICountryFeatures.validation.siretLength per company countryCode. */
const CREATE_COMPANY_SIRET_LENGTH: Record<string, number> = {
    "fr-FR": 14,
    "fr-BE": 10,
    "fr-CA": 10,
    "fr-PF": 14,
    "fr-CH": 14,
    "en-UK": 8,
    "en-IN": 8,
    "en-GR": 9,
};

/** i18n bundles that override AdminPanel / BeneficiarInfo labels per country. */
const CREATE_COMPANY_SIRET_I18N_LANG: Partial<Record<string, string>> = {
    "fr-FR": "fr",
    "fr-BE": "fr-BE",
    "fr-CA": "fr-CA",
    "fr-PF": "fr",
    "fr-CH": "fr",
    "en-UK": "en-UK",
    "en-IN": "en-IN",
    "en-GR": "en-GR",
};

type TFunction = (key: string, options?: Record<string, unknown>) => string;

export function normalizeCompanySiretDigits(siret: string): string {
    return siret.replace(/[\s.-]/g, "").replace(/\D/g, "");
}

export function getCreateCompanySiretLength(countryCode: string): number | null {
    return CREATE_COMPANY_SIRET_LENGTH[countryCode] ?? null;
}

export function validateCreateCompanySiret(siret: string, countryCode: string): boolean {
    if (!siret) return true;

    const siretLength = getCreateCompanySiretLength(countryCode);
    if (siretLength == null) return true;

    const digits = normalizeCompanySiretDigits(siret);
    return digits.length === siretLength && /^\d+$/.test(digits);
}

/** React Hook Form validator — returns `true` or an i18n error message. */
export function validateCreateCompanySiretFormValue(siret: string, countryCode: string, t: TFunction): true | string {
    if (!siret || !countryCode) return true;
    return validateCreateCompanySiret(siret, countryCode) ? true : t("InvalidSiretFormat");
}

export function getCompanySiretFormatDescription(countryCode: string, t: TFunction): string | null {
    if (!countryCode || getCreateCompanySiretLength(countryCode) == null) return null;

    if (countryCode === "en-GR" && i18n.language.startsWith("fr")) {
        return t("Settings.SiretFormatHintEnGR");
    }

    const countryLang = CREATE_COMPANY_SIRET_I18N_LANG[countryCode];
    const tCountry = countryLang ? i18n.getFixedT(countryLang) : t;
    const siretLength = getCreateCompanySiretLength(countryCode) ?? 14;

    return tCountry("Settings.SiretFormatHint", {
        count: siretLength,
        defaultValue: t("Settings.SiretFormatHint", { count: siretLength }),
    });
}

export function getCreateCompanySiretField(countryCode: string, t: TFunction) {
    if (!countryCode) {
        return {
            label: t("AdminPanel.CompanyRegistrationNumber"),
            placeholder: t("AdminPanel.SelectCountryFirstPlaceholder"),
            siretLength: null,
            description: null,
        };
    }

    const siretLength = getCreateCompanySiretLength(countryCode) ?? 14;
    const countryLang = CREATE_COMPANY_SIRET_I18N_LANG[countryCode];
    const tCountry = countryLang ? i18n.getFixedT(countryLang) : t;

    const label =
        countryCode === "en-GR" && i18n.language.startsWith("fr")
            ? t("AdminPanel.SiretEnGR")
            : tCountry("AdminPanel.Siret", {
                  defaultValue: tCountry("BeneficiarInfo.Siret", { defaultValue: t("AdminPanel.Siret") }),
              });

    const placeholder = tCountry("NewBeneficiary.CompanyCodeHere", {
        defaultValue: "0".repeat(siretLength),
    });

    return {
        label,
        placeholder,
        siretLength,
        description: getCompanySiretFormatDescription(countryCode, t),
    };
}
